<template>
  <div class="auth-wrapper">
    <!-- Since billing is not an auth view, we have a header missing:)-->
    <header class="auth-header">
      <div class="auth-container">
        <div class="auth-content-container auth-content-container--md">
          <div class="d-flex align-center">
            <v-img :src="appLogo" max-height="20" max-width="110" alt="logo" contain></v-img>
          </div>
        </div>
        <v-btn v-if="$can('view', 'billing logout')" plain small @click="logoutHandler">
          <img :src="openInNewIcon" />
          <div class="signout-text">{{ $t("auth.signout") }}</div>
        </v-btn>
      </div>
    </header>

    <div class="auth-container">
      <div class="auth-content-container">
        <div class="billing-header">
          <h2 class="billing-title">
            {{ isBasicAvailable ? $t("billing.finish_payment_title_basic") : $t("billing.finish_payment_title") }}
          </h2>
          <div class="billing-subtitle">
            {{
              isBasicAvailable
                ? $t("billing.finish_payment_subtitle_basic")
                : $t("auth.create_account") + ": " + $t("auth.current_step", { step: 2, maxSteps: 2 })
            }}
          </div>
          <div class="auth-step-indicator">
            <div v-for="loopStep in 2" :class="'primary'" :key="loopStep" class="auth-step-indicator__line"></div>
          </div>
        </div>
      </div>
    </div>

    <div class="auth-container">
      <div class="auth-content-container">
        <div class="billing-container">
          <!----Left column (plans)-->
          <div class="billing-plans">
            <div class="billing-plans-wrapper">
              <!--Billing plan item -->
              <div
                v-for="plan in paymentPlans"
                :key="plan.code"
                @click="selectPlan(plan)"
                class="billing-plan-item-wrapper"
                :class="[selectedPlan.code === plan.code ? 'billing-plan-item--selected' : '']">
                <div class="billing-plan-item">
                  <div class="billing-plan-item__info">
                    <div class="billing-plan-item__name">{{ plan.name }}</div>
                    <div class="billing-plan-item__discount" v-if="plan.discounted">
                      <span class="billing-plan-item__discount--price">${{ plan.preDiscountPrice }}/month</span>
                      <v-chip :color="'info-120'" :text-color="'info'">{{ plan.discount.discountLabel }}</v-chip>
                    </div>
                    <div class="billing-plan-item__pricing">
                      <span class="billing-plan-item__pricing-currency">$</span>
                      <span class="billing-plan-item__pricing-amount">{{ plan.price }}</span>
                      <span class="billing-plan-item__pricing-duration">/month</span>
                    </div>
                    <v-chip class="billing-plan-item__disclaimer" :color="'success-120'" :text-color="'success'">
                      {{ $t("billing.plan_disclaimer") }}
                    </v-chip>
                    <v-chip
                      v-if="plan.additionalChip"
                      class="billing-plan-item__additional-chip"
                      :color="plan.additionalChip.color"
                      :text-color="plan.additionalChip.textColor">
                      {{ plan.additionalChip.text }}
                    </v-chip>
                    <div class="billing-plan-item__feature-list">
                      <div class="billing-plan-item__feature-list-item">
                        <v-icon size="18" :color="'icon'">mdi-check</v-icon>
                        <span
                          class="billing-plan-item__feature"
                          v-html="$tc('change_plan.agency.features.domains', plan.formattedDomainsLimit)"></span>
                      </div>
                      <div class="billing-plan-item__feature-list-item">
                        <v-icon size="18" :color="'icon'">mdi-check</v-icon>
                        <span
                          class="billing-plan-item__feature"
                          v-html="$tc('change_plan.agency.features.teammates', plan.formattedTeammatesLimit)"></span>
                      </div>
                      <div class="billing-plan-item__feature-list-item">
                        <v-icon size="18" :color="'icon'">mdi-check</v-icon>
                        <span class="billing-plan-item__feature">{{ plan.uniqueBenefit }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="billing-plan-item__selection">
                    <CheckmarkIcon v-if="selectedPlan.code === plan.code"></CheckmarkIcon>
                    <EmptyCheckmarkIcon v-else></EmptyCheckmarkIcon>
                  </div>
                </div>
              </div>
              <!--Billing plan item END-->
              <div
                v-if="Object.keys(hiddenPlans).length > 0"
                class="show-hidden-plans"
                @click="hiddenPlansOpen = !hiddenPlansOpen">
                {{ hiddenPlansOpen ? $t("billing.collapse_more_plans") : $t("billing.show_more_plans") }}
                <v-icon size="24" color="primary">mdi-chevron-down {{ hiddenPlansOpen && "mdi-rotate-180" }}</v-icon>
              </div>
              <div v-if="hiddenPlansOpen" class="spacing-top--12">
                <div
                  v-for="plan in hiddenPlans"
                  :key="plan.code"
                  @click="selectPlan(plan)"
                  class="billing-plan-item-wrapper"
                  :class="[selectedPlan.code === plan.code ? 'billing-plan-item--selected' : '']">
                  <div class="billing-plan-item">
                    <div class="billing-plan-item__info">
                      <div class="billing-plan-item__name">{{ plan.name }}</div>
                      <div class="billing-plan-item__discount" v-if="plan.discounted">
                        <span class="billing-plan-item__discount--price">${{ plan.preDiscountPrice }}/month</span>
                        <v-chip :color="'info-120'" :text-color="'info'">{{ plan.discount.discountLabel }}</v-chip>
                      </div>
                      <div class="billing-plan-item__pricing">
                        <span class="billing-plan-item__pricing-currency">$</span>
                        <span class="billing-plan-item__pricing-amount">{{ plan.price }}</span>
                        <span class="billing-plan-item__pricing-duration">/month</span>
                      </div>
                      <v-chip class="billing-plan-item__disclaimer" :color="'success-120'" :text-color="'success'">
                        {{ $t("billing.plan_disclaimer") }}
                      </v-chip>
                      <div class="billing-plan-item__feature-list">
                        <div class="billing-plan-item__feature-list-item">
                          <v-icon size="18" :color="'icon'">mdi-check</v-icon>
                          <span
                            class="billing-plan-item__feature"
                            v-html="$tc('change_plan.agency.features.domains', plan.formattedDomainsLimit)"></span>
                        </div>
                        <div class="billing-plan-item__feature-list-item">
                          <v-icon size="18" :color="'icon'">mdi-check</v-icon>
                          <span
                            class="billing-plan-item__feature"
                            v-html="$tc('change_plan.agency.features.teammates', plan.formattedTeammatesLimit)"></span>
                        </div>
                        <div class="billing-plan-item__feature-list-item">
                          <v-icon size="18" :color="'icon'">mdi-check</v-icon>
                          <span class="billing-plan-item__feature">{{ plan.uniqueBenefit }}</span>
                        </div>
                      </div>
                    </div>
                    <div class="billing-plan-item__selection">
                      <CheckmarkIcon v-if="selectedPlan.code === plan.code"></CheckmarkIcon>
                      <EmptyCheckmarkIcon v-else></EmptyCheckmarkIcon>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="feature-review">
              <a target="_blank" href="https://siterunner.com/pricing">{{ $t("billing.click_here") }}</a>
              {{ $t("billing.all_features") }}
            </div>
            <div class="faq-item">
              <div class="faq-item__title">{{ $t("billing.faq_item_cancel_question") }}</div>
              <div class="faq-item__subtitle">
                {{ $t("billing.faq_item_cancel_answer") }}
              </div>
            </div>

            <div class="faq-item">
              <div class="faq-item__title">{{ $t("billing.faq_item_change_plan_question") }}</div>
              <div class="faq-item__subtitle">
                {{ $t("billing.faq_item_change_plan_answer") }}
              </div>
            </div>
            <div class="mt-6">
              <img class="mr-2" src="@/assets/icons/mastercard.svg" />
              <img class="mr-2" src="@/assets/icons/visa.svg" />
              <img src="@/assets/icons/ssl_secure.svg" />
            </div>
          </div>
          <!----Left column (plans) END-->

          <!----Right column (ccform)-->
          <v-card class="billing-info">
            <StripeForm
              @submit="handleStripeFormSubmit"
              ref="stripeForm"
              :purchase-item="selectedPlan"
              :default-values="$store.state.user"
              :onBlur="track"
              display-company-switch></StripeForm>

            <v-btn
              class="spacing-top--24"
              :loading="formLoading"
              @click="submitStripeForm"
              min-height="48"
              min-width="100%"
              color="primary"
              >{{ $t("auth.complete_payment") }}</v-btn
            >
            <p class="billing-disclaimer">
              {{ $t("billing.billing_disclaimer") }}
            </p>
          </v-card>
          <!----Right column (ccform) END-->
        </div>
      </div>
    </div>

    <footer width="auto" class="footer-text">
      © {{ new Date().getFullYear() }} <a :href="'https://' + appDomain">{{ appDomain }}</a
      >.
      <a class="text--subtitle" :href="'https://' + appDomain + '/terms-of-use'">{{ $t("generic.terms") }}</a>
      {{ $t("generic.and") }}
      <a class="text--subtitle" :href="'https://' + appDomain + '/privacy-policy'">
        {{ $t("generic.privacy_policy") }}
      </a>
    </footer>
    <cookie-bar v-if="$can('view', 'cookie bar')"></cookie-bar>
  </div>
</template>

<script>
  // Components
  import StripeForm from "@/components/forms/StripeForm.vue";
  import CheckmarkIcon from "@/components/icons/CheckmarkIcon.vue";
  import EmptyCheckmarkIcon from "@/components/icons/EmptyCheckmarkIcon.vue";
  import CookieBar from "@/components/custom/CookieBar.vue";
  // Constant
  import { PAYMENT_PLANS } from "@/constant";
  // Tracking
  import { mixpanelWrapper } from "@/@core/libs/mixpanel";
  // Icons
  import { mdiLogoutVariant } from "@mdi/js";
  import openInNewIcon from "@/assets/icons/open_in_new.svg";
  // Utils
  import store from "@/store";
  import themeConfig from "@themeConfig";
  import definedAbility from "@/ability";
  import { mapActions } from "vuex";
  import cloneDeep from "lodash/cloneDeep";
  // Utility
  import { formErrorMessage } from "@/utility/formErrorMessage";

  export default {
    components: {
      CookieBar,
      EmptyCheckmarkIcon,
      CheckmarkIcon,
      StripeForm,
    },
    data() {
      return {
        ability: definedAbility(),
        appDomain: themeConfig.app.domain,
        appLogo: themeConfig.app.logo,
        paymentPlans: {},
        hiddenPlans: {},
        selectedPlan: {},
        discounts: [],
        formLoading: false,
        PAYMENT_PLANS,
        billingTracked: false,
        hiddenPlansOpen: false,
        mdiLogoutVariant,
        openInNewIcon,
      };
    },
    beforeMount() {
      // Pre-fill cardholder name;
      const { id, email, firstName, lastName } = this.$store.state.user;
      // https://developer.drip.com/#identifying-visitors
      this.drip.push([
        "identify",
        {
          user_id: id,
          email,
          first_name: firstName,
          last_name: lastName,
          step: 2,
        },
      ]);

      this.discounts = this.$store.state.paymentDiscounts;
      this.hiddenPlans = this.getHiddenPlans();
      this.paymentPlans = this.getPlans();

      this.selectedPlan =
        this.paymentPlans[this.$store.state.billingPlan.toUpperCase()] ||
        this.paymentPlans[PAYMENT_PLANS.PRO.name.toUpperCase()];
    },
    computed: {
      isBasicAvailable() {
        return this.$store.state?.billingPlan?.toUpperCase() === PAYMENT_PLANS.BASIC.name.toUpperCase();
      },
    },
    methods: {
      ...mapActions({
        logout: "auth/logout",
      }),
      getPlans() {
        const plans = Object.fromEntries(
          Object.entries(PAYMENT_PLANS).filter(([key]) => !Object.keys(this.hiddenPlans).includes(key)),
        );

        if (!this.isBasicAvailable) {
          delete plans[PAYMENT_PLANS.BASIC.name.toUpperCase()];
        }

        return this.applyDiscounts(plans);
      },
      getHiddenPlans() {
        if (this.$store.state?.billingPlan?.toUpperCase() === PAYMENT_PLANS.BASIC.name.toUpperCase()) {
          return this.applyDiscounts(
            Object.fromEntries(
              Object.entries(PAYMENT_PLANS).filter(([key]) => key !== PAYMENT_PLANS.BASIC.name.toUpperCase()),
            ),
          );
        }

        return {};
      },
      applyDiscounts(plans) {
        if (!this.discounts.length) return plans;

        Object.keys(plans).map(planKey => {
          const planData = plans[planKey];
          const discount = this.discounts.find(discount => discount.appliesTo.includes(planData.name));
          plans[planKey] = {
            ...planData,
            ...(() => {
              // If there is a discount applied to a plan, override the price and add discount info;
              if (discount) {
                return {
                  discounted: true,
                  discount,
                  preDiscountPrice: planData.price,
                  price: planData.price - discount.discountAmount,
                };
              } else {
                return {};
              }
            })(),
          };
        });

        return plans;
      },
      selectPlan(plan) {
        this.selectedPlan = plan;
      },
      track(field) {
        if (this.billingTracked) return;
        // Wait for validation to finish
        setTimeout(() => {
          if (field.valid) {
            mixpanelWrapper.track("Payment Information Filled", { previous_page: store.getters["getPreviousRoute"] });
            this.billingTracked = true;
          }
        }, 0);
      },
      submitStripeForm() {
        this.$refs.stripeForm.submit();
      },
      /**
       * Following this payment scenario:
       * https://docs.stripe.com/payments/accept-a-payment-deferred?platform=web&type=subscription
       */
      async handleStripeFormSubmit(stripeData) {
        const { addressElement, stripe, elements, taxId, company } = stripeData;

        this.formLoading = true;
        // pro / agency / standard
        const plan = this.selectedPlan.name.toLowerCase();
        const promotionCode = this.selectedPlan.discounted ? this.selectedPlan.discount.couponCode : undefined;

        const { value: addressData } = await addressElement.getValue();
        const { address, name } = addressData;

        const billingInformation = {
          name,
          // TaxID is required for invoice generation
          taxId,
          city: address.city,
          country: address.country,
          line1: address.line1,
          line2: null,
          postalCode: address.postal_code,
          state: address.state,
          company,
        };

        try {
          const { data } = await this.createSubscription(plan, promotionCode, billingInformation);
          const { intentType, clientSecret } = data;

          const confirmIntent = intentType === "setup" ? stripe.confirmSetup : stripe.confirmPayment;

          const selectedPlan = cloneDeep(this.selectedPlan);
          delete selectedPlan.additionalChip;

          /**
           * Confirm subscription to Stripe
           * https://docs.stripe.com/payments/accept-a-payment-deferred?platform=web&type=subscription#confirm-the-subscription
           */
          const result = await confirmIntent({
            elements,
            clientSecret,
            confirmParams: {
              return_url: `${window.location.origin}/complete-payment?billingInformation=${JSON.stringify(
                billingInformation,
              )}&selectedPlan=${JSON.stringify(selectedPlan)}`,
            },
            redirect: "if_required",
          });
          /**
           * Card declined / verification failed / CVC errors etc.
           */
          if (result.error) {
            this.drip.push(["track", "Card Verification Failed"]);
            mixpanelWrapper.track("Card Verification Failed", { previous_page: store.getters["getPreviousRoute"] });
            this.$admin.toast.error(result.error.message || this.$t("generic.error"));
          } else {
            /**
             * For specific payment methods, a redirect might be triggered.
             * If redirect is triggered, the remaining code will not be executed.
             * TODO: in the future, depending on the payment methods we might have, implement
             * return_url route to handle the redirect and finalize the payment.
             */
            const intentId = intentType === "setup" ? result.setupIntent.id : result.paymentIntent.id;
            /**
             * Make purchase confirm request to API.
             * This is not a Stripe request. Organization is created here in BE;
             */
            try {
              const purchaseConfirmResponse = await this.confirmPurchase(intentId, intentType);
              const subscriptionId = purchaseConfirmResponse.data.id;
              // Handle successful purchase - tracking, redirects etc.
              this.onSuccessfulPurchase({ subscriptionId, ...billingInformation });
            } catch (e) {
              this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));

              return;
            }
          }
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.formLoading = false;
        }
      },
      /**
       * @param {Object} data Any additional data that may be needed for tracking
       */
      async onSuccessfulPurchase(data) {
        const { id, email, phoneNumber: phone } = this.$store.state.user;

        // Set "Owner" Role when payment is successful and as soon as organization is created successfully
        this.drip.push([
          "identify",
          {
            user_id: id,
            email,
            step: 2,
            phone,
            address1: data.line1,
            city: data.city || "",
            state: data.state,
            zip: data.postalCode,
            country: data.country,
            Role: "Owner",
          },
        ]);

        // Push purchase event to GTM
        this.gtm.push({
          event: "purchase",
          plan_value: this.selectedPlan.price,
        });

        // Conversion for Google Ads free trial start
        window.gtag &&
          window.gtag("event", "conversion", {
            send_to: "AW-16455619013/AHIsCNK846AZEMWj06Y9",
            value: this.selectedPlan.price,
            currency: "USD",
            transaction_id: data.subscriptionId,
          });

        // Reset discounts on successful payment
        this.$store.commit("SET_PAYMENT_DISCOUNTS", []);

        // Refresh user data - so that the organization is set and we can access the dashboard.
        await store.dispatch("refreshAndStoreAllUserData");

        mixpanelWrapper.track("Onboarding Started", { previousPage: store.getters["getPreviousRoute"] });

        if (process.env.VUE_APP_DRIP_ENABLED === "true" && this.ability.can("view", "drip survey modal")) {
          this.$store.commit("SET_DISPLAY_DRIP_SURVEY_MODAL", true);
        }

        // Redirect to the dashboard
        this.$router.push({ name: "dashboard" });
      },

      /**
       * Subscription created but organization does not exist here yet.
       * https://docs.stripe.com/payments/accept-a-payment-deferred?platform=web&type=subscription#create-intent
       */
      async createSubscription(plan, promotionCode, billingInformation) {
        const response = await this.$admin.dataProvider.customHttpCall("POST", "subscriptions", {
          plan,
          promotionCode,
          billingInformation,
        });

        return response;
      },
      /**
       * This is where the organization is actually created in BE;
       * For now, organization name is by default "<First Name>'s Organization"
       */
      async confirmPurchase(intentId, intentType) {
        // GA Client ID is used for tracking in Backend.
        const gaClientId = window.gaGlobal?.vid || "";
        const response = await this.$admin.dataProvider.customHttpCall("POST", "subscriptions/confirm", {
          intentId,
          intentType,
          gaClientId,
        });

        return response;
      },
      async logoutHandler() {
        window.location.href = "https://siterunner.com/";
        this.logout();
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/functions.scss";
  @import "@core/preset/preset/typography/index.scss";

  .auth-header {
    max-width: 1440px;
    margin: 0 auto !important;
  }
  .signout-text {
    @include text-body-1();
    margin-left: 8px;
  }
  .auth-container {
    display: flex;
    padding: 4px;
    @media (min-width: 800px) {
      padding: 0;
    }
    .auth-content-container {
      width: 100%;
      margin: 0 auto;
      @media (min-width: 800px) {
        width: 778px;
      }
      &--md {
        width: 100%;
      }
    }
  }

  .billing-header {
    margin-bottom: 24px;
    @media (min-width: 800px) {
      margin-bottom: 32px;
    }
    .billing-title {
      @include heading-4();
      color: get-color("headline");
      margin-bottom: 4px;
      @media (min-width: 800px) {
        @include heading-3();
        margin-bottom: 16px;
      }
    }
    .billing-subtitle {
      @include text-body-2();
      color: get-color("subtitle");
      margin-bottom: 16px;
    }
    .auth-step-indicator {
      display: flex;
      gap: 20px;
      &__line {
        height: 5px;
        border-radius: 4px;
        background: get-color("primary-20");
        flex: 1;
      }
    }
  }

  .billing-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
    @media (min-width: 800px) {
      flex-direction: row;
      gap: 32px;
    }
    .billing-plans {
      @media (min-width: 800px) {
        width: 348px;
      }
      .billing-plans-wrapper {
        .billing-plan-item-wrapper {
          border: 2px solid get-color("same-white");

          &:not(:last-of-type) {
            margin-bottom: 12px;
          }

          &.billing-plan-item--selected {
            border: 3px solid get-color("primary");
            border-radius: 6px;

            & .billing-plan-item {
              border: none;
            }
          }
        }

        .billing-plan-item {
          display: flex;
          background-color: get-color("same-white");
          border-radius: 6px;
          padding: 24px;
          border: 1px solid get-color("border");
          cursor: pointer;

          .billing-plan-item__info {
            flex: 1;
            .billing-plan-item__name {
              @include heading-3();
              color: get-color("headline");
            }
            .billing-plan-item__pricing {
              margin-top: 2px;
              &-currency {
                @include heading-3();
                color: get-color("headline");
              }
              &-amount {
                @include heading-2();
                color: get-color("headline");
                margin: 0 1px;
              }
              &-duration {
                @include text-subtitle-1();
                color: get-color("headline");
              }
            }
            .billing-plan-item__discount {
              display: flex;
              align-items: center;
              margin-top: 2px;
              &--price {
                @include headline-m();
                text-decoration: line-through;
              }
              &::v-deep {
                .v-chip {
                  height: 28px;
                  margin-left: 8px;
                  .v-chip__content {
                    @include text-body-2-medium();
                  }
                }
              }
            }
            .billing-plan-item__disclaimer {
              height: 24px;
              margin: 6px 0;
              &::v-deep {
                .v-chip__content {
                  @include text-caption();
                }
              }
            }

            .billing-plan-item__additional-chip {
              height: 24px;
              margin: 6px 0 6px 4px;
              &::v-deep {
                .v-chip__content {
                  @include text-caption();
                }
              }
            }

            .billing-plan-item__feature-list {
              &-item {
                display: flex;
                align-items: center;
                &:not(:last-of-type) {
                  margin-bottom: 1px;
                }
              }
              .billing-plan-item__feature {
                @include text-body-2();
                color: get-color("body");
                margin-left: 8px;
              }
            }
          }
        }
      }
      .feature-review {
        @include text-body-1();
        color: get-color("body");
        margin: 12px 0;
        a {
          text-decoration: none;
          color: get-color("primary");
        }
      }
      .faq-item {
        &:not(:last-of-type) {
          margin-bottom: 12px;
        }
        &__title {
          @include text-body-2-bold();
          color: get-color("headline");
        }
        &__subtitle {
          @include text-body-2();
          color: get-color("body");
        }
      }
    }
    .billing-info {
      width: 100%;
      padding: 32px;
      align-self: baseline;
      @media (min-width: 800px) {
        width: 398px;
      }
      .billing-disclaimer {
        @include text-caption();
        color: get-color("body");
        margin-top: 12px;
      }
    }
  }
  .billing-label {
    @include text-body-2();
    color: get-color("body");
  }

  header {
    padding: 0 0 12px 0;

    .auth-content-container {
      display: flex;
      align-items: center;
    }
    @media (min-width: 800px) {
      padding: 0 12px 12px 12px;
    }
  }

  footer {
    padding: 32px 0 17px 0;
    margin-top: 42px;
    &.footer-text {
      color: get-color("subtitle");
      font-size: 14px;
      line-height: 24px;
      text-align: center;

      a {
        color: get-color("subtitle") !important;
      }
    }
  }

  .user-menu {
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    @include text-body-1();
    color: get-color("body");
  }

  .show-hidden-plans {
    color: get-color("primary");
    @include text-body-2-medium();
    cursor: pointer;
    user-select: none;
  }
</style>
